import { Fragment, type ReactNode } from 'react';

import { Divider, HelpCircleIcon, Text } from '@thefarmersfront/kitchen';

import dayjs, { type Dayjs } from 'dayjs';

import { useTooltipStore } from '~/store';
import { formatDate } from '~/utils/date';

import styles from './DeliveryAgencyReportUpdateInfo.module.scss';

const PLACEHOLDER = '-';
const DATE_FORMAT = 'YYYY.MM.DD HH:mm';
const DEFAULT_UPDATE_CYCLE = ['0:30', '7:30', '8:30', '10:30', '12:30'];
const DEFAULT_UPDATE_CYCLE_TOOLTIP = '하루배송 결과는 익일 오전에 확인 부탁드립니다.';

const toMinutes = (time: string): number => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + (minute || 0);
};

const toScheduledTime = (now: Dayjs, slotMinutes: number, dayOffset: number): Dayjs =>
  now
    .add(dayOffset, 'day')
    .hour(Math.floor(slotMinutes / 60))
    .minute(slotMinutes % 60)
    .second(0)
    .millisecond(0);

const getUpdateTimes = (cycle: string[], now: Dayjs): { last: Dayjs | null; next: Dayjs | null } => {
  if (cycle.length === 0) return { last: null, next: null };

  const slots = cycle.map(toMinutes);
  const nowMinutes = now.hour() * 60 + now.minute();
  const lastSlotToday = [...slots].reverse().find((slot) => slot <= nowMinutes);
  const nextSlotToday = slots.find((slot) => slot > nowMinutes);

  return {
    last:
      lastSlotToday !== undefined
        ? toScheduledTime(now, lastSlotToday, 0)
        : toScheduledTime(now, slots[slots.length - 1], -1),
    next: nextSlotToday !== undefined ? toScheduledTime(now, nextSlotToday, 0) : toScheduledTime(now, slots[0], 1),
  };
};

interface DeliveryAgencyReportUpdateInfoProps {
  updateCycle?: string[];
  updateCycleTooltip?: ReactNode;
}

/**
 * [배송 결과 리포트] 마지막/다음 업데이트 시각 및 업데이트 주기 표기 컴포넌트
 */
const DeliveryAgencyReportUpdateInfo = ({
  updateCycle = DEFAULT_UPDATE_CYCLE,
  updateCycleTooltip = DEFAULT_UPDATE_CYCLE_TOOLTIP,
}: DeliveryAgencyReportUpdateInfoProps) => {
  const { tooltip, setTooltip } = useTooltipStore();

  const handleMouseEnter = () => {
    setTooltip({ show: true, description: updateCycleTooltip });
  };

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, show: false });
  };

  const { last, next } = getUpdateTimes(updateCycle, dayjs());

  const items: { label: string; value: string; hasTooltip?: boolean }[] = [
    { label: '마지막 업데이트', value: formatDate(last, DATE_FORMAT) ?? PLACEHOLDER },
    { label: '다음 업데이트', value: formatDate(next, DATE_FORMAT) ?? PLACEHOLDER },
    { label: '업데이트 주기', value: updateCycle.join(', ') || PLACEHOLDER, hasTooltip: true },
  ];

  return (
    <div className={styles.container}>
      {items.map(({ label, value, hasTooltip }, index) => (
        <Fragment key={label}>
          {index > 0 && <Divider orientation="vertical" className={styles.divider} />}
          <div className={styles.item}>
            <Text size="small" weight="semi-bold" color="tertiary">
              {label}
            </Text>
            {hasTooltip && updateCycleTooltip && (
              <HelpCircleIcon
                className={styles['tooltip-icon']}
                size={16}
                color="gray400"
                data-tooltip-id="react-tooltip"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )}
            <Text size="small" weight="semi-bold" color="secondary">
              {value}
            </Text>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export { DeliveryAgencyReportUpdateInfo };
